/**
 * Number formatting for physics results: significant figures and scientific notation.
 *
 * Physics answers run from 10⁻³¹ kg to 10³⁰ kg, so results are written to a chosen number
 * of significant figures — plain for everyday magnitudes, `6.674 × 10⁻¹¹` beyond — in the
 * reader's decimal mark.
 */

export type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const PLAIN_LOW = 1e-3;
const PLAIN_HIGH = 1e6;
const MAX_SIG = 12;

/** An integer exponent in Unicode superscripts: −8 → "⁻⁸". */
export function superscriptInt(value: number): string {
  let out = value < 0 ? "⁻" : "";
  for (const digit of String(Math.abs(value))) {
    out += SUPERSCRIPT_DIGITS[Number(digit)];
  }
  return out;
}

/** `2.58 × 10²³`, with the mantissa in the reader's decimal mark. */
export function scientific(
  fmt: NumberFormatter,
  value: number,
  sig: number
): string {
  const [mantissa, exponent] = value.toExponential(sig - 1).split("e");
  const shown = fmt(Number(mantissa), {
    minimumFractionDigits: sig - 1,
    maximumFractionDigits: sig - 1,
    useGrouping: false,
  });
  return `${shown} × 10${superscriptInt(Number(exponent))}`;
}

/**
 * Round to `sig` significant figures and write it: plain from 0.001 up to 999,999,
 * scientific notation beyond. Zero is "0".
 */
export function formatSig(
  fmt: NumberFormatter,
  value: number,
  sig: number
): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (value === 0) {
    return fmt(0);
  }
  const clamped = Math.min(Math.max(Math.round(sig), 1), MAX_SIG);
  const magnitude = Math.abs(value);
  if (magnitude < PLAIN_LOW || magnitude >= PLAIN_HIGH) {
    return scientific(fmt, value, clamped);
  }
  return fmt(value, {
    minimumSignificantDigits: clamped,
    maximumSignificantDigits: clamped,
    useGrouping: false,
  });
}

/** A value echoed in the working: five significant figures, no float noise. */
export function formatEcho(fmt: NumberFormatter, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (value === 0) {
    return fmt(0);
  }
  const magnitude = Math.abs(value);
  if (magnitude < PLAIN_LOW || magnitude >= PLAIN_HIGH) {
    const [mantissa, exponent] = value.toExponential(4).split("e");
    const shown = fmt(Number(mantissa), {
      maximumFractionDigits: 4,
      useGrouping: false,
    });
    return `${shown} × 10${superscriptInt(Number(exponent))}`;
  }
  return fmt(value, { maximumSignificantDigits: 6, useGrouping: false });
}
